import { Divider, Layout, Modal, notification, Typography } from 'antd';
import * as React from 'react';
import SchGConsultantsApiService from '../../api/SchGConsultantsApiService';
import ScheduleApiService from '../../api/ScheduleApiService';
import Dr2AmendedIndicators from '../../consts/Dr2AmendedIndicators';
import Dr2AmendedStatuses from '../../consts/Dr2AmendedStatuses';
import NoteEntityParents from '../../consts/NoteEntityParents';
import Routes from '../../consts/Routes';
import ScheduleAbbreviationCodes from '../../consts/ScheduleAbbreviationCodes';
import SchGConsultantsDTO from '../../models/SchGConsultantsDTO';
import CurrentUser from '../../utils/CurrentUser';
import HistoryUtil from '../../utils/HistoryUtil';
import DataTable, { DataTableColumnProps } from '../shared/DataTable/DataTable';
import DataTableButtonUtil from '../shared/DataTable/DataTableButtonUtil';
import DataTableColumnUtil from '../shared/DataTable/DataTableColumnUtil';
import NotesModal from '../shared/NotesModal';
import Dr2StatusBar from './Dr2StatusBar';
import NumberFormatUtil from '../../utils/NumberFormatUtil';
import Role from '../../consts/Role';

const { Content } = Layout;
const { Text } = Typography;
const { confirm } = Modal;

interface SchGConsultantsProps {
}

interface SchGConsultantsState {
  tableColumns: DataTableColumnProps<SchGConsultantsDTO>[];
  numConsultants: number;
  totalOfSched: number;
  flagNoteId: string;
  conSummId: string;
  showModal: boolean;
}

class SchGConsultants extends React.Component<SchGConsultantsProps, SchGConsultantsState> {
  constructor(props: SchGConsultantsProps) {
    super(props);

    this.state = {
      tableColumns: this.getTableColumns(),
      numConsultants: 0,
      totalOfSched: 0,
      flagNoteId: '',
      conSummId: '',
      showModal: false,
    };
  }

  private dataTable: DataTable<SchGConsultants> | undefined;

  componentDidMount() {
    this.getStatistics();
  }

  render() {
    const { numConsultants, totalOfSched, flagNoteId, conSummId,showModal } = this.state;
    const actionButtons = [];
    actionButtons.push(DataTableButtonUtil.Reset());
    actionButtons.push(DataTableButtonUtil.Primary(
      'New Consultant',
      () => HistoryUtil.push(Routes.generate(Routes.ADD_SCHEDULE_G_CONSULTANT, {}))
    ));

    const currUser = CurrentUser.Get();

    return (
     <>
      <Content className="content-pad">
        <Dr2StatusBar dr2Id={currUser?.dr2Id || ''} />
        <Text strong>
          Number of Consultants: {numConsultants}&emsp;|&emsp;Total of Schedule: {NumberFormatUtil.currency(totalOfSched)}&emsp;|
        </Text>
        <NotesModal parentId={currUser?.dr2Id || ''} parent={NoteEntityParents.SCHEDULE} scheduleCd={ScheduleAbbreviationCodes.SCHEDULEG} />
        <Divider />
        <DataTable
          buttonBar={actionButtons}
          columns={this.state.tableColumns}
          fetchData={{
            fetch: function (tableRequest) {
              return SchGConsultantsApiService.getSchGConsultants(tableRequest, currUser?.dr2Id || '');
            },
            failureMessage: 'Failed to retrieve consultants'
          }}
          globalSearch={true}
          ref={(element: any) => (this.dataTable = element)}
          serverSide={true}
          styleOptions={{ compact: true, alternatingRowHighlight: true }}
          tableProps={{
            rowKey: 'consultantSummaryIdAndSeq',
            sortDirections: ['ascend', 'descend'],
            locale: { emptyText: 'Currently there are no Consultants to manage.' }
          }}
          title="Schedule G - Consultants"
          stateSaving={{
              enabled: true,
              tableUniqueKey: 'manageSchGConsultants',
              perSession: true,
          }}
        />
        </Content>
        {showModal &&
          <NotesModal
            parentId={conSummId || ''}
            parent={NoteEntityParents.CONSULTANTSUMMARY}
            auditorsNoteId={flagNoteId}
            canFlag={true}
            onClose={() => { this.setState({ showModal: false }, () => this.dataTable?.refresh()); }} />
        }
      </>
    );
  }

  private getStatistics = () => {
    ScheduleApiService.getScheduleCount(CurrentUser.Get()?.dr2Id || '', ScheduleAbbreviationCodes.SCHEDULEG)
      .then(numConsultants => {
        this.setState({ numConsultants });
      }).catch(error => {
        notification.error({
          message: 'Error while fetching the number of expenditures',
          description: error.message
        });
      });

    ScheduleApiService.getScheduleTotal(CurrentUser.Get()?.dr2Id || '', ScheduleAbbreviationCodes.SCHEDULEG)
      .then(totalOfSched => {
        this.setState({ totalOfSched });
      }).catch(error => {
        notification.error({
          message: 'Error while fetching the total of schedule',
          description: error.message
        });
      });
  }

  private getTableColumns = (): DataTableColumnProps<SchGConsultantsDTO>[] => {
    return [
      DataTableColumnUtil.Date('From Date', 'fromDate', 100, { defaultSortOrder: 'ascend' }),
      DataTableColumnUtil.Date('To Date', 'toDate', 100),
      DataTableColumnUtil.Address('Consultant', 'consultantName',
        (c) => ({
          name: c.consultantName,
          line1: c.consultantAddressLine1,
          line2: c.consultantAddressLine2,
          city: c.consultantCity,
          state: c.consultantState,
          zip: c.consultantZip
        })
      ),
      DataTableColumnUtil.Currency('Amount', 'compensationAmt'),
      DataTableColumnUtil.DropdownMulti('Status', 'status',
        [
          { text: Dr2AmendedStatuses.ORIGINAL, value: Dr2AmendedIndicators.ORIGINAL },
          { text: Dr2AmendedStatuses.AMENDED, value: Dr2AmendedIndicators.AMENDED },
          { text: Dr2AmendedStatuses.ADJUSTED, value: Dr2AmendedIndicators.ADJUSTED },
          { text: Dr2AmendedStatuses.DELETED, value: Dr2AmendedIndicators.DELETED }
        ]),
      DataTableColumnUtil.Description('Description', 'explanation'),
      DataTableColumnUtil.Text('Last Updated By', 'updatedBy'),
      DataTableColumnUtil.Date('Last Updated On', 'updatedOn'),
      DataTableColumnUtil.FlagButton('Flagged', 'flaggedNoteId', CurrentUser.Get()?.isInRole(Role.IECDB_ADMINISTRATOR) || false, [
        {
          onClick: (rowData) => this.openFlagModal(rowData.flaggedNoteId || '', rowData.consultantSummaryId || ''),
          visible: (rowData) => rowData.flaggedNoteId != null
        }
      ]),
      DataTableColumnUtil.Buttons('consultantSummaryIdAndSeq',
        [
          {
            text: 'Edit',
            onClick: (rowData) =>
              HistoryUtil.push(Routes.generate(
                Routes.EDIT_SCHEDULE_G_CONSULTANT,
                {
                  summaryId: rowData.consultantSummaryId || '',
                  summarySeqNum: rowData.seqNumber || ''
                }))
          },
          {
            text: 'Delete',
            onClick: (rowData) => this.confirmDelete(
              rowData.consultantSummaryId || '',
              rowData.seqNumber || 0,
              rowData.consultantName || '',
              rowData.compensationAmt || 0)
          },
          {
            text: 'Expenses',
            onClick: (rowData) =>
              HistoryUtil.push(Routes.generate(
                Routes.SCHEDULE_G_CONSULTANT_EXPENSES,
                {
                  summaryId: rowData.consultantSummaryId || '',
                  summarySeqNum: rowData.seqNumber || ''
                }
              ))
          }
        ],
        220)
    ];
  }

  private openFlagModal = (flagNoteId: string, conSummId: string) => {
    this.setState({ showModal: true, flagNoteId, conSummId }, () => this.dataTable?.refresh());
  }

  private showTableAndRefresh = () => {
    if (this.dataTable) {
      this.dataTable.refresh();
    }
    this.getStatistics();
  }

  private confirmDelete = (consultantSummaryId: string, seqNum: number, consultantName: string, amount: number) => {
    confirm({
      title: 'Are you sure you want to delete this consultant?',
      content: 'The following record will be permanently deleted: ' + consultantName + ' - $' + amount,
      onOk: () => {
        this.deleteContribution(consultantSummaryId, seqNum);
      },
    });
  }

  private deleteContribution = (consultantSummaryId: string, seqNum: number) => {
    SchGConsultantsApiService.delete(consultantSummaryId, seqNum)
      .then(() => {
        this.showTableAndRefresh();
        notification.success({
          message: 'Deleted Successfully'
        });
      }).catch(() => {
        notification.error({
          message: 'Error while deleting consultant',
          description: '',
        });
      });
  }
}

export default SchGConsultants;